"use client";
import React from "react";
import {
    Sparkles, MessageSquareMore, LayoutTemplate, Plus, CopyPlus,
    Settings, Save, Trash2, Eye, FileText, Database, Server,
    Code, Play, History, Box, ArrowRight, User, Shield, ShoppingCart, Receipt, MessageSquare, Heart
} from "lucide-react";

// --- Mockup Container ---
const MockupContainer = ({ title, children }: { title: string, children: React.ReactNode }) => (
    <div className="w-full bg-surface rounded-xl border border-border shadow-lg overflow-hidden flex flex-col">
        <div className="bg-background/50 border-b border-border px-4 py-2 flex items-center gap-2">
            <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-400/80" />
                <div className="w-3 h-3 rounded-full bg-amber-400/80" />
                <div className="w-3 h-3 rounded-full bg-green-400/80" />
            </div>
            <span className="ml-2 text-xs font-medium text-secondary-text">{title}</span>
        </div>
        <div className="p-6 bg-surface relative min-h-[200px] flex items-center justify-center">
            {children}
        </div>
    </div>
);

// --- SRS Mockup ---
export const SRSMockup = () => (
    <MockupContainer title="SRS Generation">
        <div className="w-full max-w-md space-y-4">
            <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <Sparkles className="w-4 h-4 text-primary" />
                </div>
                <div className="space-y-1 bg-background border border-border p-3 rounded-lg rounded-tl-none shadow-sm">
                    <p className="text-sm text-primary-text">Describe your application idea...</p>
                    <p className="text-xs text-secondary-text">"I want a project management tool with tasks, users, and comments."</p>
                </div>
            </div>
            <div className="flex items-center gap-2 justify-center py-2">
                <div className="h-px bg-border flex-1" />
                <span className="text-[10px] font-mono text-secondary-text uppercase">AI Processing</span>
                <div className="h-px bg-border flex-1" />
            </div>
            <div className="bg-background border border-border rounded-lg p-4 space-y-2 shadow-sm">
                <div className="flex items-center gap-2 text-primary text-sm font-medium">
                    <FileText className="w-4 h-4" /> Generated Schema
                </div>
                <div className="space-y-1.5 pl-6 border-l-2 border-border">
                    <div className="text-xs text-primary-text">User (id, name, email)</div>
                    <div className="text-xs text-primary-text">Task (id, title, status, assignee_id)</div>
                    <div className="text-xs text-primary-text">Comment (id, content, task_id)</div>
                </div>
                <button className="w-full mt-2 py-1.5 bg-primary text-white text-xs font-medium rounded hover:bg-primary/90 transition-colors">
                    Apply Schema
                </button>
            </div>
        </div>
    </MockupContainer>
);

// --- Chat Mockup ---
export const ChatMockup = () => (
    <MockupContainer title="AI Schema Chat">
        <div className="w-full max-w-md flex flex-col h-[240px] bg-background border border-border rounded-lg overflow-hidden shadow-sm">
            <div className="flex-1 p-4 space-y-3 overflow-y-auto">
                <div className="flex justify-end">
                    <div className="bg-primary text-white px-3 py-2 rounded-lg rounded-tr-none text-xs max-w-[80%]">
                        Add a 'deadline' field to the Task model.
                    </div>
                </div>
                <div className="flex justify-start">
                    <div className="bg-surface border border-border px-3 py-2 rounded-lg rounded-tl-none text-xs text-primary-text max-w-[80%]">
                        Done. I've added a <code>deadline</code> field of type <code>Date</code> to the <code>Task</code> model.
                    </div>
                </div>
            </div>
            <div className="p-3 border-t border-border bg-surface flex gap-2">
                <div className="flex-1 h-8 bg-background border border-border rounded px-2 flex items-center text-xs text-secondary-text">
                    Ask AI to modify schema...
                </div>
                <div className="w-8 h-8 bg-primary rounded flex items-center justify-center text-white">
                    <ArrowRight className="w-4 h-4" />
                </div>
            </div>
        </div>
    </MockupContainer>
);

// --- Template Mockup ---
export const TemplateMockup = () => (
    <MockupContainer title="Template Selection">
        <div className="grid grid-cols-2 gap-4 w-full max-w-lg">
            {[
                { name: "E-commerce", icon: Box, color: "bg-blue-500" },
                { name: "SaaS Starter", icon: Server, color: "bg-purple-500" },
                { name: "Blog / CMS", icon: FileText, color: "bg-green-500" },
                { name: "Social App", icon: MessageSquareMore, color: "bg-pink-500" }
            ].map((t) => (
                <div key={t.name} className="bg-background border border-border p-3 rounded-lg hover:border-primary/50 cursor-pointer transition-all group">
                    <div className={`w-8 h-8 ${t.color}/10 rounded-lg flex items-center justify-center mb-2 group-hover:scale-110 transition-transform`}>
                        <t.icon className={`w-4 h-4 ${t.color.replace('bg-', 'text-')}`} />
                    </div>
                    <div className="text-sm font-medium text-primary-text">{t.name}</div>
                    <div className="text-[10px] text-secondary-text mt-1">Pre-built schema & API</div>
                </div>
            ))}
        </div>
    </MockupContainer>
);

// --- Model Template Mockup ---
export const ModelTemplateMockup = () => (
    <MockupContainer title="Model Library">
        <div className="grid grid-cols-2 gap-3 w-full max-w-lg">
            {[
                { name: "User", icon: User, color: "bg-blue-500" },
                { name: "Auth", icon: Shield, color: "bg-purple-500" },
                { name: "Orders", icon: ShoppingCart, color: "bg-green-500" },
                { name: "Bills", icon: Receipt, color: "bg-yellow-500" },
                { name: "Comments", icon: MessageSquare, color: "bg-pink-500" },
                { name: "Likes", icon: Heart, color: "bg-red-500" }
            ].map((t) => (
                <div key={t.name} className="bg-background border border-border p-2.5 rounded-lg hover:border-primary/50 cursor-pointer transition-all group flex items-center gap-3">
                    <div className={`w-8 h-8 ${t.color}/10 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform`}>
                        <t.icon className={`w-4 h-4 ${t.color.replace('bg-', 'text-')}`} />
                    </div>
                    <div className="text-sm font-medium text-primary-text">{t.name}</div>
                </div>
            ))}
        </div>
    </MockupContainer>
);

// --- Node Mockup ---
export const NodeMockup = () => (
    <MockupContainer title="Schema Node Entity">
        <div className="w-64 bg-background border border-border rounded-lg shadow-md overflow-hidden">
            <div className="bg-primary/5 border-b border-primary/10 px-3 py-2 flex items-center justify-between">
                <span className="font-bold text-sm text-primary-text">User</span>
                <Settings className="w-3 h-3 text-secondary-text cursor-pointer hover:text-primary" />
            </div>
            <div className="p-2 space-y-1">
                {[
                    { name: "id", type: "UUID", key: true },
                    { name: "email", type: "String", unique: true },
                    { name: "password", type: "String", secure: true },
                    { name: "role", type: "Enum", def: "'user'" },
                    { name: "created_at", type: "DateTime" }
                ].map((field) => (
                    <div key={field.name} className="flex items-center justify-between px-2 py-1.5 hover:bg-surface rounded text-xs group">
                        <div className="flex items-center gap-2">
                            {field.key && <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />}
                            <span className="text-primary-text font-mono">{field.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-secondary-text opacity-70">{field.type}</span>
                            {field.unique && <span className="text-[9px] px-1 bg-blue-100 text-blue-700 rounded">UQ</span>}
                        </div>
                    </div>
                ))}
                <div className="pt-1 border-t border-border mt-1">
                    <div className="flex items-center gap-1 text-xs text-secondary-text hover:text-primary cursor-pointer px-2 py-1">
                        <Plus className="w-3 h-3" /> Add Field
                    </div>
                </div>
            </div>
        </div>
    </MockupContainer>
);

// --- Toolbar Mockup ---
export const ToolbarMockup = () => (
    <MockupContainer title="Workspace Toolbar">
        <div className="flex flex-col gap-4 items-center bg-surface p-4 rounded-xl border border-border shadow-sm">
            <div className="flex flex-col gap-2">
                <div className="w-10 h-10 bg-background border border-border rounded-lg flex items-center justify-center text-secondary-text hover:text-primary hover:border-primary/50 cursor-pointer transition-colors" title="Save">
                    <Save className="w-5 h-5" />
                </div>
                <div className="w-10 h-10 bg-background border border-border rounded-lg flex items-center justify-center text-secondary-text hover:text-red-500 hover:border-red-500/50 cursor-pointer transition-colors" title="Delete">
                    <Trash2 className="w-5 h-5" />
                </div>
                <div className="w-10 h-10 bg-primary/10 border border-primary/20 rounded-lg flex items-center justify-center text-primary cursor-pointer transition-colors" title="Preview Mode">
                    <Eye className="w-5 h-5" />
                </div>
            </div>
        </div>
    </MockupContainer>
);

// --- Code Gen Mockup ---
export const CodeGenMockup = () => (
    <MockupContainer title="Backend Code Preview">
        <div className="w-full max-w-2xl bg-[#1e1e1e] rounded-lg overflow-hidden shadow-xl font-mono text-xs">
            <div className="flex items-center justify-between px-4 py-2 bg-[#252526] border-b border-[#3e3e42]">
                <div className="flex gap-2">
                    <span className="text-blue-400">user.controller.ts</span>
                </div>
                <div className="flex gap-2">
                    <div className="w-2 h-2 rounded-full bg-yellow-500" />
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                </div>
            </div>
            <div className="p-4 text-gray-300 space-y-1">
                <div><span className="text-purple-400">import</span> {"{ Controller, Get, Post }"} <span className="text-purple-400">from</span> <span className="text-orange-300">'@nestjs/common'</span>;</div>
                <div className="h-2" />
                <div><span className="text-yellow-300">@Controller</span>(<span className="text-orange-300">'users'</span>)</div>
                <div><span className="text-purple-400">export class</span> <span className="text-yellow-300">UserController</span> {"{"}</div>
                <div className="pl-4">
                    <div><span className="text-yellow-300">@Get</span>()</div>
                    <div><span className="text-blue-300">findAll</span>() {"{"}</div>
                    <div className="pl-4"><span className="text-purple-400">return</span> <span className="text-blue-300">this</span>.userService.<span className="text-yellow-300">findAll</span>();</div>
                    <div>{"}"}</div>
                </div>
                <div>{"}"}</div>
            </div>
        </div>
    </MockupContainer>
);
